import React, { useState } from 'react'
import * as Yup from 'yup'
import clsx from 'clsx'
import { FastField, Form, Formik, FormikProps, FormikHelpers } from 'formik'
import { useMutation } from 'react-query'
import { ExclamationIcon } from '@heroicons/react/outline'
import toast from 'react-hot-toast'
import { useNavigate } from 'react-router-dom'

import authService from '../../services/authService'
import { SignIn, SignInError } from '../../types/sign-in'

import FormikInput from '../FormikInput'
import Spinner from '../Spinner'
import { Link } from 'react-router-dom'

// Sign-in only checks that something was entered. Composition rules belong on set/reset, where
// they shape a new password; enforcing them here locks out every account created before the
// policy and blames the user's capital letters for what is really a rejected credential.
const schema = Yup.object().shape({
  email: Yup.string().email('Must be a valid email').required('Email is required'),
  password: Yup.string().required('Password is required'),
})

const initialValues: SignIn = {
  email: '',
  password: '',
}

const SignInForm: React.FC = () => {
  const [isBlocked, setBlocked] = useState<boolean>(false)
  const navigate = useNavigate()

  const signIn = useMutation<void, SignInError, SignIn>(authService.loginWithCredentials, {
    onMutate: () => {
      setBlocked(false)
    },
  })

  const handleOnSubmit = async (values: SignIn, formikBag: FormikHelpers<SignIn>): Promise<void> => {
    try {
      // Firebase is the only authority on whether these credentials are good. Asking the API
      // whether the email exists first leaked that answer to unauthenticated callers, and the
      // probe ran without a token, so it failed with a permission error for legitimate users.
      // Authorisation is enforced after sign-in by the role-claim check in Auth.tsx.
      await signIn.mutateAsync(values)

      navigate('/')
    } catch (e) {
      const error = e as SignInError
      switch (error.code) {
        // The codes that actually mean "these credentials are wrong". They must all read the
        // same, so that none of them narrows down whether an address has an account.
        case 'auth/user-not-found':
        case 'auth/wrong-password':
        case 'auth/invalid-email':
        case 'auth/invalid-credential':
          formikBag.setFieldError('email', 'The email or password you entered is incorrect')
          break
        case 'i-user/user-is-blocked':
          setBlocked(true)
          break
        case 'auth/user-disabled':
          formikBag.setFieldError('email', 'This account has been disabled. Please contact your administrator')
          break
        case 'auth/too-many-requests':
          formikBag.setFieldError(
            'email',
            'Access to this account has been temporarily disabled due to many failed login attempts. You can immediately restore it by resetting your password or you can try again later'
          )
          break
        // Everything else — permission-denied, network and internal errors — is a failure of the
        // service, not of the credentials, so it must not land on a credential field. Toasting it
        // follows ResetPasswordForm, which reports service-level failures the same way.
        default:
          toast.error('We could not sign you in. Please try again, or contact support if this continues.')
          break
      }
    }
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={schema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={handleOnSubmit}
    >
      {({ isSubmitting }: FormikProps<SignIn>) => (
        <Form className="space-y-6" noValidate>
          {isBlocked ? (
            <div className="rounded-md bg-yellow-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <ExclamationIcon className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800">Organisation Inactive</h3>
                  <div className="mt-2 text-sm text-yellow-700">
                    <p>
                      Please note that the billing for this organisation has failed or expired. Contact the organisation
                      admin to request billing be updated.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          ) : null}
          <fieldset disabled={isSubmitting}>
            <div className="space-y-3">
              <FastField
                type="email"
                name="email"
                component={FormikInput}
                label="Email"
                autoComplete="username"
                autoFocus
              />
              <FastField
                type="password"
                name="password"
                component={FormikInput}
                label="Password"
                autoComplete="current-password"
              />
            </div>
          </fieldset>

          <div>
            <button
              className={clsx({
                'btn btn-primary btn-full': true,
                'animate__animated animate__shakeX': isBlocked,
              })}
              disabled={isSubmitting}
            >
              {isSubmitting ? <Spinner className="trailing-icon h-4 w-4" /> : 'Sign in'}
            </button>
          </div>
          <div className="text-center">
            <Link
              to="/auth/reset-password"
              className="text-sm text-brand-500 underline transition-colors duration-200 hover:text-brand-700"
            >
              Reset password / Update password
            </Link>
          </div>
        </Form>
      )}
    </Formik>
  )
}

export default SignInForm
